import Vue from 'vue';
import moment from 'moment-timezone';
import { isEqual } from 'lodash-es';
import conflictDetectorWorker from '@/location/conflict-detector.worker';

const conflictDetector = conflictDetectorWorker();

export default function useActionBookingStationTime (
  booking,
  bookings,
  startTime,
  bookingStationTimes,
  bookingStationTimesInStore,
  bookingStationTimesChanges,
  experiences,
  getExperienceStations,
  getExperienceTiers,
  tierPriceToLocationPrice,
  editBooking,
) {
  function editBookingStationTime (id, property, value) {
    const bookingTimeStationInStore = bookingStationTimesInStore.value.find(bookingStationTime => bookingStationTime.id === id);
    const dirty = (bookingTimeStationInStore) ? !isEqual(bookingTimeStationInStore[property], value) : true;
    if (dirty) {
      if (!bookingStationTimesChanges.value[id]) {
        Vue.set(bookingStationTimesChanges.value, id, {
          id,
          _id: id,
          [property]: value,
        });
      } else {
        Vue.set(bookingStationTimesChanges.value[id], property, value);
      }
    } else if (bookingStationTimesChanges.value[id]) {
      Vue.delete(bookingStationTimesChanges.value[id], property);
    }
  }

  // Find the next station that ideally wont cause a conflict, and that already isn't in the reservation
  async function findNextStation (experienceIndex) {
    const amountOfStations = experiences.value.reduce((stations, experience) => {
      return [...stations, ...getExperienceStations(experience.id)];
    }, []).length;
    const conflict = {};
    let count = 0;

    const findNextStationRecursive = async (experienceIndex, stationIndex, countUp = true) => {
      if (count > amountOfStations) return;
      if (countUp) count++;

      const experience = experiences.value[experienceIndex];
      const stations = getExperienceStations(experience.id);
      const station = stations[stationIndex];
      const nextStationIndex = (stationIndex === stations.length - 1) ? 0 : stationIndex + 1;

      if (!station) {
        const nextExperienceIndex = (experienceIndex === experiences.value.length - 1) ? 0 : experienceIndex + 1;
        return findNextStationRecursive(nextExperienceIndex, 0, false);
      }

      const alreadyInBooking = !!bookingStationTimes.value.find(bookingStationTime => bookingStationTime.station.id === station.id);
      if (alreadyInBooking) {
        return findNextStationRecursive(experienceIndex, nextStationIndex);
      }

      const tier = getExperienceTiers(experience.id)[0];
      const wouldCauseConflict = await conflictDetector.willStartTimeStationAndTierCauseConflict(startTime.value, station, tier, booking.value._id, bookings.value);

      if (
        (wouldCauseConflict && !conflict.station) ||
        (wouldCauseConflict && conflict.station && station.id !== conflict.station.id)
      ) {
        conflict.experience = conflict.experience || experience;
        conflict.station = conflict.station || station;
        return findNextStationRecursive(experienceIndex, nextStationIndex);
      }

      return {
        experience,
        station,
      };
    };

    const foundStation = await findNextStationRecursive(experienceIndex, 0);
    if (!foundStation && !conflict.station) throw new Error('No valid experience or station available for new group.');

    return foundStation || conflict;
  }

  async function addBookingStationTime (experienceId, prefill) {
    const experienceIndex = (experienceId) ? experiences.value.findIndex(experience => experience.id === experienceId) : 0;
    const { experience, station } = prefill || await findNextStation(experienceIndex, experienceIndex, 0, 0);

    const tier = getExperienceTiers(experience.id)[0];

    // unique enough pseudo id generation
    const newId = (prefill && prefill._id) ? prefill._id : `new${Math.floor(Math.random() * 1000000000000)}`;

    Vue.set(bookingStationTimesChanges.value, newId, {
      _id: newId,
      experience: {
        id: experience.id,
      },
      station: {
        id: station.id,
      },
      tier,
      amountDue: tierPriceToLocationPrice(tier.price),
      endTime: moment(startTime).add(tier.length, 'minutes').format(),
      amountPaid: 0,
      discount: {},
      coupon: {},
      startedAt: null,
      ...prefill,
    });

    if (booking.numPlayers < bookingStationTimes.length) {
      editBooking('numPlayers', bookingStationTimes.length);
    }
  }

  function removeBookingStationTime (bookingStationTimeId) {
    if (bookingStationTimeId.includes('new')) {
      Vue.delete(bookingStationTimesChanges.value, bookingStationTimeId);
    } else {
      editBookingStationTime(bookingStationTimeId, '_removed', true);
    }
  }

  async function undoRemoveBookingStationTime (bookingStationTimeId) {
    Vue.delete(bookingStationTimesChanges.value, bookingStationTimeId);
    const bookingStationTime = bookingStationTimes.value.find(bookingStationTime => bookingStationTime.id === bookingStationTimeId);
    const needsANewStation = bookingStationTimes.value.find(existingBookingStationTime => {
      return existingBookingStationTime !== bookingStationTime && existingBookingStationTime.station.id === bookingStationTime.station.id;
    });
    if (needsANewStation) {
      const experienceIndex = experiences.value.findIndex(experience => experience.id === bookingStationTime.experience.id);
      const { experience, station } = await findNextStation(experienceIndex, experienceIndex, 0, 0);

      const tier = getExperienceTiers(experience.id)[0];

      editBookingStationTime(bookingStationTime.id, 'station', { id: station.id });
      editBookingStationTime(bookingStationTime.id, 'experience', { id: experience.id });
      editBookingStationTime(bookingStationTime.id, 'tier', tier);
    }
  }

  return {
    editBookingStationTime,
    addBookingStationTime,
    removeBookingStationTime,
    undoRemoveBookingStationTime,
  };
}
